import React, { useMemo, useState } from 'react';
import { Tooltip, message as antdMessage } from 'antd';
import AuthMedia from '../files/AuthMedia';
import ImageLightbox from '../files/ImageLightbox';
import { API_URL } from '../../../config';
import chatStore from '../stores/chatStore';
import { parseSizeString, simplifyRatio } from '../utils/imageSize';
import { getCachedImageBlob } from '../utils/imageBlobCache';

async function fetchImageBlob(path) {
    const token = localStorage.getItem('token');
    const full = path?.startsWith('http') ? path : `${API_URL}${path}`;
    return getCachedImageBlob(full, async () => {
        const res = await fetch(full, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error('Не удалось загрузить изображение');
        return res.blob();
    });
}

const ActionBtn = ({ label, tooltip, onClick, children, className = '', disabled = false }) => (
    <Tooltip title={tooltip} placement="right" mouseEnterDelay={0.45}>
        <button
            type="button"
            aria-label={label}
            disabled={disabled}
            onClick={(e) => {
                e.stopPropagation();
                onClick?.(e);
            }}
            className={`group/action flex h-[4.5rem] max-w-[4.5rem] items-center overflow-hidden rounded-full bg-black/55 text-white shadow-md backdrop-blur-sm transition-[max-width,background-color,box-shadow] duration-300 ease-out hover:max-w-[15rem] hover:bg-black/75 hover:shadow-lg focus-visible:max-w-[15rem] focus-visible:bg-black/75 disabled:opacity-50 ${className}`}
        >
            <span className="flex h-[4.5rem] w-[4.5rem] shrink-0 items-center justify-center [&_svg]:h-7 [&_svg]:w-7">
                {children}
            </span>
            <span
                className="min-w-0 overflow-hidden whitespace-nowrap pr-5 text-[15px] font-medium tracking-wide opacity-0 transition-opacity duration-200 ease-out delay-75 group-hover/action:opacity-100 group-focus-visible/action:opacity-100"
            >
                {label}
            </span>
        </button>
    </Tooltip>
);

const GeneratedImage = ({ image, model, message, className = '', priority = 'auto' }) => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(null);
    const [natural, setNatural] = useState(null);
    const path = image.url;

    const sizeLabel = useMemo(() => {
        const gp = message?.generation_params || {};
        const fromSize = parseSizeString(gp.size);
        const width =
            Number(image.width) ||
            Number(gp.width) ||
            fromSize?.width ||
            natural?.width ||
            0;
        const height =
            Number(image.height) ||
            Number(gp.height) ||
            fromSize?.height ||
            natural?.height ||
            0;
        if (!width || !height) return null;

        const ratio =
            gp.aspect_ratio ||
            message?.aspect_ratio ||
            simplifyRatio(width, height);
        return ratio ? `${width}×${height} · ${ratio}` : `${width}×${height}`;
    }, [image.width, image.height, message, natural]);

    const onDownload = async () => {
        setBusy('download');
        try {
            const blob = await fetchImageBlob(path);
            const obj = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = obj;
            a.download = `bananasgpt-${image.file_id}.${(image.mime || 'image/png').split('/')[1] || 'png'}`;
            a.click();
            URL.revokeObjectURL(obj);
            antdMessage.success('Скачано');
        } catch (e) {
            antdMessage.error(e.message || 'Ошибка скачивания');
        } finally {
            setBusy(null);
        }
    };

    const onCopy = async () => {
        setBusy('copy');
        try {
            const blob = await fetchImageBlob(path);
            const type = blob.type || image.mime || 'image/png';
            await navigator.clipboard.write([new ClipboardItem({ [type]: blob })]);
            antdMessage.success('Скопировано в буфер');
        } catch (e) {
            antdMessage.error(e.message || 'Не удалось скопировать');
        } finally {
            setBusy(null);
        }
    };

    const onReference = async () => {
        setBusy('reference');
        try {
            await chatStore.useImageAsReference(image);
            antdMessage.success('Добавлено как референс');
        } catch (e) {
            antdMessage.error(e.message || 'Не удалось добавить референс');
        } finally {
            setBusy(null);
        }
    };

    const onRecreate = async () => {
        setBusy('recreate');
        try {
            await chatStore.recreateImageGeneration(message);
            antdMessage.success('Промпт и файлы подставлены');
        } catch (e) {
            antdMessage.error(e.message || 'Не удалось восстановить данные');
        } finally {
            setBusy(null);
        }
    };

    const onTelegram = async () => {
        setBusy('telegram');
        try {
            const blob = await fetchImageBlob(path);
            // Telegram игнорирует файл, если в share есть title/text — только files
            let mime = blob.type || image.mime || 'image/png';
            if (!mime.startsWith('image/') || mime === 'application/octet-stream') {
                mime = 'image/png';
            }
            const ext = (mime.split('/')[1] || 'png').split(';')[0].replace('jpeg', 'jpg');
            const file = new File([blob], `bananasgpt-${image.file_id}.${ext}`, { type: mime });
            const shareData = { files: [file] };

            if (typeof navigator.canShare === 'function' && navigator.canShare(shareData)) {
                try {
                    await navigator.share(shareData);
                    return;
                } catch (e) {
                    if (e?.name === 'AbortError') return;
                }
            }

            // Fallback (часто десктоп): скачать файл и открыть Telegram — вложить вручную
            const obj = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = obj;
            a.download = file.name;
            a.click();
            URL.revokeObjectURL(obj);
            window.open('tg://', '_blank');
            antdMessage.info('Изображение скачано — вложите его в выбранный чат Telegram');
        } catch (e) {
            antdMessage.error(e.message || 'Не удалось отправить в Telegram');
        } finally {
            setBusy(null);
        }
    };

    return (
        <div
            className={`bananas-img-preview group relative overflow-hidden rounded-xl border border-black/10 dark:border-white/10 ${className}`}
        >
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="block w-full overflow-hidden rounded-xl"
            >
                {path ? (
                    <AuthMedia
                        src={path}
                        alt=""
                        priority={priority}
                        className="h-auto w-full object-contain"
                        onLoad={(e) => {
                            const el = e.currentTarget;
                            if (el?.naturalWidth && el?.naturalHeight) {
                                setNatural({ width: el.naturalWidth, height: el.naturalHeight });
                            }
                        }}
                    />
                ) : (
                    <div className="flex h-40 w-full items-center justify-center text-sm opacity-50">
                        Нет URL изображения
                    </div>
                )}
            </button>

            {/* Кнопки поверх изображения — масштаб от размера превью */}
            <div className="pointer-events-none absolute inset-0 z-10 p-3 opacity-100 transition sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                <div className="bananas-img-actions pointer-events-auto flex flex-col items-start gap-2.5">
                    <ActionBtn
                        label="Download"
                        tooltip="Скачать изображение"
                        onClick={onDownload}
                        disabled={busy === 'download'}
                    >
                        <svg viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path d="M12 3v12m0 0l4-4m-4 4l-4-4M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </ActionBtn>
                    <ActionBtn
                        label="Copy"
                        tooltip="Скопировать изображение в буфер обмена"
                        onClick={onCopy}
                        disabled={busy === 'copy'}
                    >
                        <svg viewBox="0 0 24 24" fill="none" aria-hidden>
                            <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
                            <path d="M5 15V5a2 2 0 012-2h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </ActionBtn>
                    <ActionBtn
                        label="Telegram"
                        tooltip="Отправить изображение контакту в Telegram"
                        onClick={onTelegram}
                        disabled={busy === 'telegram'}
                    >
                        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                            <path d="M21.5 3.3L2.8 10.5c-1.3.5-1.3 1.2-.2 1.5l4.8 1.5 1.9 5.8c.2.7.4.9 1 .9.5 0 .8-.2 1.1-.5l2.3-2.2 4.8 3.5c.9.5 1.5.2 1.7-.8l3.1-14.7c.3-1.3-.5-1.9-1.6-1.5zM9.2 14.1l-.3 3.5 1.5-2.1 7.6-6.8c.3-.2.6 0 .3.3l-8.1 7.1z" />
                        </svg>
                    </ActionBtn>
                    <ActionBtn
                        label="Reference"
                        tooltip="Вставить это изображение в следующий промпт как референс"
                        onClick={onReference}
                        disabled={busy === 'reference'}
                    >
                        <svg viewBox="0 0 24 24" fill="none" aria-hidden>
                            <rect x="3" y="5" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
                            <path d="M17 9h2a2 2 0 012 2v8a2 2 0 01-2 2h-8a2 2 0 01-2-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            <circle cx="10" cy="11" r="1.5" fill="currentColor" />
                            <path d="M5 17l3.5-3.5L12 16l2-2 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </ActionBtn>
                    <ActionBtn
                        label="Recreate"
                        tooltip="Подставить в промпт исходный текст и файлы этой генерации"
                        onClick={onRecreate}
                        disabled={busy === 'recreate'}
                    >
                        <svg viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path d="M4 4v6h6M20 20v-6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M20 9A8 8 0 006.5 6.5L4 10M4 15a8 8 0 0013.5 2.5L20 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </ActionBtn>
                </div>

                {/* Лупа — полноэкранный просмотр (lightbox в portal) */}
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="bananas-img-actions">
                        <Tooltip title="Открыть на весь экран" placement="top" mouseEnterDelay={0.25}>
                            <button
                                type="button"
                                aria-label="Открыть на весь экран"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpen(true);
                                }}
                                className="pointer-events-auto flex h-16 w-16 scale-95 items-center justify-center rounded-full bg-black/55 text-white shadow-lg backdrop-blur-sm transition duration-200 hover:scale-100 hover:bg-black/75"
                            >
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden>
                                    <circle cx="10" cy="10" r="6.25" stroke="currentColor" strokeWidth="2" />
                                    <path
                                        d="M10 7.25v5.5M7.25 10h5.5"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                    <path
                                        d="M15.25 15.25L20 20"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </button>
                        </Tooltip>
                    </div>
                </div>
            </div>

            {sizeLabel ? (
                <div className="bananas-img-actions pointer-events-none absolute bottom-2 right-2 z-10">
                    <div className="rounded-md bg-black/55 px-2 py-0.5 text-[11px] font-medium tabular-nums tracking-wide text-white/95 shadow-sm backdrop-blur-sm">
                        {sizeLabel}
                    </div>
                </div>
            ) : null}

            {open && <ImageLightbox url={path} name={image.file_id} onClose={() => setOpen(false)} />}
        </div>
    );
};

export default GeneratedImage;
